import { useState } from "react";
import { Download, Upload, Eye, TrendingUp, Package } from "lucide-react";
import { toast } from "sonner";
import PricingConfigPanel from "./PricingConfigPanel";
import EditablePctCell from "./EditablePctCell";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PreviewPage = ({ result: initialResult, onBackToUpload }) => {
  const [result, setResult] = useState(initialResult);
  const [isExporting, setIsExporting] = useState(false);
  const [showAllItems, setShowAllItems] = useState(false);
  const [showDirectLink, setShowDirectLink] = useState(false);
  const [csvContent, setCsvContent] = useState("");
  // Selection: default all items selected. Set of item IDs that are EXCLUDED.
  const [excludedIds, setExcludedIds] = useState(() => new Set());

  const isSelected = (id) => !excludedIds.has(id);
  const toggleItem = (id) => {
    setExcludedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAll = () => setExcludedIds(new Set());
  const deselectAll = () => setExcludedIds(new Set(result.items.map((i) => i.id)));

  // Comma-separated string of selected item IDs for export URLs
  const selectedItemIdsParam = () => {
    const ids = result.items.filter((i) => isSelected(i.id)).map((i) => i.id);
    return ids.join(",");
  };

  // Selected items for a specific pallet (only IDs that exist in that pallet)
  const palletSelectedIdsParam = (pallet) => {
    return pallet.items
      .filter((i) => isSelected(i.id))
      .map((i) => i.id)
      .join(",");
  };

  const handlePricingUpdated = (updated) => {
    setResult(updated);
    // Hide stale CSV preview after recalculation
    setCsvContent("");
    setShowDirectLink(false);
  };

  const handleExport = async () => {
    setIsExporting(true);
    
    try {
      // Get CSV content (only selected items)
      const ids = selectedItemIdsParam();
      if (!ids) {
        toast.error("Select at least one item to export");
        setIsExporting(false);
        return;
      }
      const url = `${API}/export-shopify/${result.id}?item_ids=${encodeURIComponent(ids)}`;
      const response = await fetch(url);
      if (response.ok) {
        const csvText = await response.text();
        setCsvContent(csvText);
        
        // Force download using multiple methods
        try {
          // Method 1: Blob download with forced headers
          const blob = new Blob([csvText], { 
            type: 'text/csv;charset=utf-8;' 
          });
          
          // Create download link
          const link = document.createElement('a');
          const url = URL.createObjectURL(blob);
          link.setAttribute('href', url);
          link.setAttribute('download', `shopify_import_${result.filename}`);
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          
          toast.success("File download started! Check your Downloads folder.");
        } catch (downloadError) {
          console.warn('Blob download failed:', downloadError);
          
          // Method 2: Data URI fallback
          try {
            const dataStr = "data:text/csv;charset=utf-8," + encodeURIComponent(csvText);
            const downloadAnchorNode = document.createElement('a');
            downloadAnchorNode.setAttribute("href", dataStr);
            downloadAnchorNode.setAttribute("download", `shopify_import_${result.filename}`);
            document.body.appendChild(downloadAnchorNode);
            downloadAnchorNode.click();
            downloadAnchorNode.remove();
            
            toast.success("File download started using fallback method!");
          } catch (fallbackError) {
            console.warn('Data URI download failed:', fallbackError);
            toast.error("Automatic download failed. Please use manual copy method below.");
          }
        }
        
        setShowDirectLink(true);
      } else {
        throw new Error('Failed to get CSV content');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error("Failed to export CSV file");
      setShowDirectLink(true);
    } finally {
      setIsExporting(false);
    }
  };

  const getCategoryBadgeClass = (category) => {
    const categoryLower = category.toLowerCase();
    if (categoryLower.includes('baby') || categoryLower.includes('care')) {
      return 'category-badge category-badge-baby';
    } else if (categoryLower.includes('apparel') || categoryLower.includes('clothing')) {
      return 'category-badge category-badge-apparel';
    }
    return 'category-badge category-badge-general';
  };

  const getPricingPercentage = (category) => {
    const categoryLower = category.toLowerCase();
    if (categoryLower.includes('baby') || categoryLower.includes('care')) {
      return '70%';
    } else if (categoryLower.includes('apparel') || categoryLower.includes('clothing')) {
      return '50%';
    }
    return '65%';
  };

  const totalRetailValue = result.items.reduce((sum, item) => sum + item.retail_price * item.quantity, 0);
  const totalSellingValue = result.items.reduce((sum, item) => sum + item.selling_price * item.quantity, 0);
  const totalCostValue = result.items.reduce((sum, item) => sum + item.cost_price * item.quantity, 0);
  const totalProfit = result.items.reduce((sum, item) => sum + item.profit_amount * item.quantity, 0);

  const displayItems = showAllItems ? result.items : result.items.slice(0, 10);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between space-y-4 lg:space-y-0">
        <div>
          <h1 
            className="text-4xl sm:text-5xl font-black tracking-tight heading-font" 
            data-testid="preview-title"
          >
            Ready for Export
          </h1>
          <p className="text-lg text-slate-600 mono-font mt-2" data-testid="preview-subtitle">
            {result.processed_items} items • {result.filename}
          </p>
        </div>
        
        <div className="flex space-x-3">
          <button 
            onClick={onBackToUpload}
            className="refinery-button-secondary"
            data-testid="back-to-upload-button"
          >
            <Upload className="w-4 h-4 mr-2" />
            New Upload
          </button>
          
          <button 
            onClick={handleExport}
            disabled={isExporting}
            className="refinery-button flex items-center"
            data-testid="export-csv-button"
          >
            {isExporting ? (
              <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            {isExporting ? 'Exporting...' : 'Export Shopify CSV'}
          </button>
          
          {showDirectLink && (
            <div className="flex flex-wrap gap-2">
              <a 
                href={`${API}/download-shopify/${result.id}?item_ids=${encodeURIComponent(selectedItemIdsParam())}`}
                className="refinery-button flex items-center"
                data-testid="force-download-link"
              >
                <Download className="w-4 h-4 mr-2" />
                Download CSV File
              </a>
              <a 
                href={`${API}/export-shopify/${result.id}?item_ids=${encodeURIComponent(selectedItemIdsParam())}`}
                download={`shopify_import_${result.filename}`}
                className="refinery-button-secondary flex items-center"
                data-testid="direct-download-link"
              >
                <Download className="w-4 h-4 mr-2" />
                Right-Click → Save As
              </a>
              <button 
                onClick={() => {
                  window.location.href = `${API}/download-shopify/${result.id}?item_ids=${encodeURIComponent(selectedItemIdsParam())}`;
                  toast.success("Redirecting to download...");
                }}
                className="refinery-button-secondary flex items-center"
                data-testid="redirect-download-button"
              >
                <Download className="w-4 h-4 mr-2" />
                Direct Download
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Pricing Configuration */}
      <PricingConfigPanel result={result} onPricingUpdated={handlePricingUpdated} />

      {/* Pallet Information */}
      {result.pallets && result.pallets.length > 0 && (
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-6 space-y-4">
          <h3 className="text-xl font-bold heading-font text-slate-900" data-testid="pallets-title">
            📦 Pallets Detected
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {result.pallets.map((pallet, index) => {
              const selectedInPallet = pallet.items.filter((i) => isSelected(i.id)).length;
              const palletParam = palletSelectedIdsParam(pallet);
              const disabled = selectedInPallet === 0;
              return (
              <div key={index} className="bg-white border border-slate-200 rounded-sm p-4" data-testid={`pallet-${index}`}>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-bold mono-font text-slate-900">Pallet {pallet.pallet_number}</h4>
                  <span className="text-sm bg-emerald-100 text-emerald-700 px-2 py-1 rounded">
                    {selectedInPallet} / {pallet.total_items} selected
                  </span>
                </div>
                <div className="space-y-2">
                  <a
                    href={disabled ? undefined : `${API}/download-pallet/${result.id}/${pallet.pallet_number}?item_ids=${encodeURIComponent(palletParam)}`}
                    onClick={(e) => { if (disabled) { e.preventDefault(); toast.error("No items selected in this pallet"); } }}
                    className={`w-full refinery-button-secondary text-sm flex items-center justify-center ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    data-testid={`download-pallet-${index}`}
                  >
                    <Download className="w-3 h-3 mr-1" />
                    Download CSV
                  </a>
                  <a
                    href={disabled ? undefined : `${API}/export-pallet/${result.id}/${pallet.pallet_number}?item_ids=${encodeURIComponent(palletParam)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => { if (disabled) { e.preventDefault(); toast.error("No items selected in this pallet"); } }}
                    className={`w-full refinery-button-secondary text-sm flex items-center justify-center text-slate-600 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    data-testid={`view-pallet-${index}`}
                  >
                    <Eye className="w-3 h-3 mr-1" />
                    View CSV
                  </a>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="total-items-summary">
          <div className="flex items-center space-x-2 mb-2">
            <Package className="w-5 h-5 text-slate-600" />
            <span className="text-sm font-medium text-slate-600 mono-font">Total Items</span>
          </div>
          <div className="text-2xl font-black heading-font">{result.processed_items}</div>
        </div>
        
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-6" data-testid="retail-value-summary">
          <div className="flex items-center space-x-2 mb-2">
            <Eye className="w-5 h-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-700 mono-font">Retail Value</span>
          </div>
          <div className="text-2xl font-black heading-font text-blue-900">${totalRetailValue.toFixed(2)}</div>
        </div>
        
        <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-6" data-testid="selling-value-summary">
          <div className="flex items-center space-x-2 mb-2">
            <TrendingUp className="w-5 h-5 text-emerald-600" />
            <span className="text-sm font-medium text-emerald-700 mono-font">Selling Value</span>
          </div>
          <div className="text-2xl font-black heading-font text-emerald-900">${totalSellingValue.toFixed(2)}</div>
        </div>
        
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-6" data-testid="profit-summary">
          <div className="flex items-center space-x-2 mb-2">
            <TrendingUp className="w-5 h-5 text-amber-600" />
            <span className="text-sm font-medium text-amber-700 mono-font">Total Profit</span>
          </div>
          <div className="text-2xl font-black heading-font text-amber-900">${totalProfit.toFixed(2)}</div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-bold heading-font flex items-center" data-testid="data-table-title">
            <Eye className="w-5 h-5 mr-2" />
            Product Data Preview
          </h3>
          <div className="flex items-center gap-3 text-sm mono-font">
            <span className="text-slate-700" data-testid="selection-summary">
              <span className="font-bold text-emerald-700">{result.items.length - excludedIds.size}</span>
              <span className="text-slate-500"> / {result.items.length} selected</span>
            </span>
            <button
              onClick={selectAll}
              className="text-emerald-700 hover:text-emerald-800 underline"
              data-testid="select-all-button"
            >
              Select all
            </button>
            <span className="text-slate-300">|</span>
            <button
              onClick={deselectAll}
              className="text-slate-600 hover:text-slate-800 underline"
              data-testid="deselect-all-button"
            >
              Deselect all
            </button>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="data-table w-full" data-testid="products-table">
            <thead>
              <tr>
                <th data-testid="column-select" className="text-center w-12">
                  <input
                    type="checkbox"
                    checked={result.items.length > 0 && excludedIds.size === 0}
                    ref={(el) => {
                      if (el) el.indeterminate = excludedIds.size > 0 && excludedIds.size < result.items.length;
                    }}
                    onChange={(e) => (e.target.checked ? selectAll() : deselectAll())}
                    className="h-4 w-4 text-emerald-600"
                    data-testid="select-all-checkbox"
                    aria-label="Select all"
                  />
                </th>
                <th data-testid="column-title">Product Title</th>
                <th data-testid="column-sku">SKU</th>
                <th data-testid="column-upc">UPC</th>
                <th data-testid="column-category">Category</th>
                <th data-testid="column-qty">Qty</th>
                <th data-testid="column-retail">Retail Price</th>
                <th data-testid="column-selling">Selling % / Price</th>
                <th data-testid="column-cost">Cost % / Price</th>
                <th data-testid="column-profit">Profit</th>
                <th data-testid="column-handle">Shopify Handle</th>
              </tr>
            </thead>
            <tbody>
              {displayItems.map((item, index) => (
                <tr key={item.id} data-testid={`product-row-${index}`} className={isSelected(item.id) ? '' : 'opacity-50 bg-slate-50'}>
                  <td className="text-center" data-testid={`product-select-cell-${index}`}>
                    <input
                      type="checkbox"
                      checked={isSelected(item.id)}
                      onChange={() => toggleItem(item.id)}
                      className="h-4 w-4 text-emerald-600"
                      data-testid={`product-select-${item.id}`}
                      aria-label={`Include ${item.description}`}
                    />
                  </td>
                  <td className="font-medium" data-testid={`product-title-${index}`}>
                    {item.description}
                  </td>
                  <td data-testid={`product-sku-${index}`}>{item.item_number}</td>
                  <td data-testid={`product-upc-${index}`}>{item.upc_number}</td>
                  <td data-testid={`product-category-${index}`}>
                    <div className="flex flex-col space-y-1">
                      <span className={getCategoryBadgeClass(item.category_description)}>
                        {getPricingPercentage(item.category_description)}
                      </span>
                      <span className="text-xs text-slate-500">
                        {item.category_description.substring(0, 20)}
                      </span>
                    </div>
                  </td>
                  <td data-testid={`product-qty-${index}`}>{item.quantity}</td>
                  <td data-testid={`product-retail-${index}`}>${item.retail_price.toFixed(2)}</td>
                  <td className="price-column" data-testid={`product-selling-${index}`}>
                    <EditablePctCell
                      resultId={result.id}
                      itemId={item.id}
                      retailPrice={item.retail_price}
                      pct={item.selling_pct}
                      price={item.selling_price}
                      field="selling_pct"
                      onUpdated={handlePricingUpdated}
                    />
                  </td>
                  <td data-testid={`product-cost-${index}`}>
                    <EditablePctCell
                      resultId={result.id}
                      itemId={item.id}
                      retailPrice={item.retail_price}
                      pct={item.cost_pct}
                      price={item.cost_price}
                      field="cost_pct"
                      onUpdated={handlePricingUpdated}
                    />
                  </td>
                  <td className="profit-column" data-testid={`product-profit-${index}`}>
                    ${item.profit_amount.toFixed(2)}
                  </td>
                  <td className="text-slate-500" data-testid={`product-handle-${index}`}>
                    {item.shopify_handle}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {result.items.length > 10 && (
          <div className="p-4 border-t border-slate-200 text-center">
            <button 
              onClick={() => setShowAllItems(!showAllItems)}
              className="text-emerald-600 hover:text-emerald-700 font-medium mono-font text-sm"
              data-testid="show-all-items-button"
            >
              {showAllItems ? 'Show Less' : `Show All ${result.items.length} Items`}
            </button>
          </div>
        )}
      </div>

      {/* CSV Content Display */}
      {csvContent && (
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-bold heading-font text-slate-900">CSV Content (Copy This)</h4>
            <button 
              onClick={() => {
                navigator.clipboard.writeText(csvContent);
                toast.success("CSV content copied to clipboard!");
              }}
              className="refinery-button-secondary text-sm"
            >
              Copy to Clipboard
            </button>
          </div>
          <div className="bg-white border border-slate-300 rounded p-4 overflow-auto max-h-96">
            <pre className="mono-font text-xs text-slate-800 whitespace-pre-wrap">{csvContent}</pre>
          </div>
          <div className="text-sm text-slate-600 space-y-2">
            <p><strong>Instructions:</strong></p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Click "Copy to Clipboard" above</li>
              <li>Open a text editor (Notepad, TextEdit, etc.)</li>
              <li>Paste the content (Ctrl+V or Cmd+V)</li>
              <li>Save the file as "shopify_import.csv"</li>
              <li>Upload the .csv file to Shopify</li>
            </ol>
          </div>
        </div>
      )}

      {/* Export Info */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-6 relative noise-overlay">
        <h4 className="font-bold heading-font mb-3 text-emerald-900" data-testid="export-info-title">
          Ready for Shopify Import
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mono-font text-sm text-emerald-800">
          <div data-testid="csv-format-info">
            <div className="font-medium mb-1">✓ CSV Format</div>
            <div className="text-emerald-700">All 47 Shopify columns included</div>
          </div>
          <div data-testid="pricing-calculated-info">
            <div className="font-medium mb-1">✓ Pricing Calculated</div>
            <div className="text-emerald-700">Category-based rules applied</div>
          </div>
          <div data-testid="handles-generated-info">
            <div className="font-medium mb-1">✓ Handles Generated</div>
            <div className="text-emerald-700">SEO-friendly product URLs</div>
          </div>
          <div data-testid="import-ready-info">
            <div className="font-medium mb-1">✓ Import Ready</div>
            <div className="text-emerald-700">No errors, ready for upload</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewPage;